import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormHelperText,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { fetchAPI } from "../../../utils/api";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const STORAGE_OPTIONS = ["LN2 Tank 1", "LN2 Tank 2"];

const AliquotStorageAssignment = () => {
  const { state } = useLocation();
  const { sampleId: pSample } = useParams();
  const navigate = useNavigate();

  const getFlow = () => {
    try {
      const fromSession =
        JSON.parse(sessionStorage.getItem("aliquotFlow")) || {};
      const base = { ...(fromSession || {}), ...(state || {}) };
      if (pSample && (!base.sampleId || base.sampleId !== pSample))
        base.sampleId = pSample;
      return base;
    } catch {
      return { ...(state || {}), sampleId: pSample };
    }
  };
  const flow = getFlow();

  const sampleId = useMemo(() => flow?.sampleId ?? "", [flow]);
  const aliquotCount = useMemo(() => flow?.aliquotCount ?? null, [flow]);
  const returnTo = flow?.returnTo || BIOBANK_ROUTES.SAMPLE_PROCESSING;

  const details = flow?.detailsForm || {};
  const processedAll = details?.processedAll ?? null;
  const processedAliquots = details?.processedAliquots ?? null;
  const aliquotComments = details?.aliquotComments ?? null;
  const sampleComments = details?.sampleComments ?? null;

  const initialStorage = STORAGE_OPTIONS.includes(flow?.storageLocation)
    ? flow.storageLocation
    : "";
  const [storageLocation, setStorageLocation] = useState(initialStorage);
  useEffect(() => {
    if (storageLocation && !STORAGE_OPTIONS.includes(storageLocation))
      setStorageLocation("");
  }, [storageLocation]);

  const [storageComments, setStorageComments] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const charsLeft = 250 - (storageComments?.length || 0);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const nextErrors = {};
    if (!storageLocation) nextErrors.storageLocation = true;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (!sampleId) {
      setError(
        "Missing required context: sampleId not found. Please go back and try again."
      );
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const body = {
        aliquotStorage: storageLocation,
        storageComments: storageComments?.trim() || "",
      };

      if (Number.isInteger(aliquotCount) && aliquotCount > 0) {
        body.totalAliquots = aliquotCount;
      }
      if (processedAll != null) {
        body.processedAll = processedAll;
      }
      if (typeof processedAliquots === "number") {
        body.processedAliquots = processedAliquots;
      }

      body.aliquotComments = aliquotComments ?? null;
      body.sampleComments = sampleComments ?? null;

      await fetchAPI(
        `biobank/samples/${encodeURIComponent(sampleId)}`,
        "PATCH",
        {},
        body
      );

      try {
        const raw = sessionStorage.getItem("processedSamples");
        const map = raw ? JSON.parse(raw) : {};
        map[sampleId] = {
          processed: true,
          storageLocation,
          updatedAt: Date.now(),
        };
        sessionStorage.setItem("processedSamples", JSON.stringify(map));
      } catch {}

      sessionStorage.removeItem("aliquotFlow");
      setTimeout(() => {
        navigate(returnTo, {
          replace: true,
          state: {
            toast: {
              type: "success",
              message: `Aliquot storage saved for ${sampleId}.`,
            },
          },
        });
      }, 100);
    } catch (err) {
      setError(err?.message || "Failed to save storage assignment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    sessionStorage.setItem("aliquotFlow", JSON.stringify(flow));
    navigate(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_BARCODE_ASSIGNMENT(encodeURIComponent(sampleId), aliquotCount || ""), {
      state: flow,
    });
  };

  return (
    <FocusedFlowPage
      title="Aliquot Storage Assignment"
      subtitle="Please indicate where the Aliquots will be stored until they are shipped to Houston."
      onClose={() => navigate(returnTo)}
      maxWidth={700}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: "#8C5A3C",
            color: "#8C5A3C",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { borderColor: "#8C5A3C", background: "#FBF6F3" },
          }}
        >
          Back
        </Button>,
        <Button
          key="submit"
          type="submit"
          form="aliquot-storage-form"
          variant="contained"
          disabled={submitting}
          sx={{
            bgcolor: "#500000",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { bgcolor: "#6a0000" },
          }}
        >
          {submitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} /> Submitting…
            </Box>
          ) : (
            "Submit"
          )}
        </Button>,
      ]}
    >
      <Snackbar
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={() => setSuccessOpen(false)}
          sx={{ fontWeight: 600 }}
        >
          {successMsg}
        </Alert>
      </Snackbar>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          id="aliquot-storage-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "grid", rowGap: 2.5, width: "100%" }}
        >
          <FormControl
            fullWidth
            size="small"
            error={!!errors.storageLocation}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root fieldset": (theme) => ({
                borderColor: errors.storageLocation
                  ? theme.palette.error.main
                  : undefined,
              }),
            }}
          >
            <InputLabel required>Storage Location</InputLabel>
            <Select
              value={storageLocation}
              label="Storage Location *"
              onChange={(e) => setStorageLocation(e.target.value)}
              displayEmpty
              sx={{ "& .MuiSelect-select": { py: 1.25 } }}
            >
              <MenuItem value="">
                <em>Select a location…</em>
              </MenuItem>
              {STORAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {errors.storageLocation && (
              <FormHelperText>Storage Location is required.</FormHelperText>
            )}
          </FormControl>

          <TextField
            label="Please leave any necessary comments about the Aliquot storage, if applicable."
            value={storageComments}
            onChange={(e) => setStorageComments(e.target.value.slice(0, 250))}
            size="small"
            fullWidth
            multiline
            minRows={4}
            inputProps={{ maxLength: 250 }}
            helperText={`${charsLeft} characters left`}
          />
        </Box>
    </FocusedFlowPage>
  );
};

export default AliquotStorageAssignment;
